/**
 * Hash a password in the browser before it is sent anywhere.
 *
 * Request payloads never carry the typed password - only this derived value,
 * which the API requires (backend/app/core/client_hash.py holds the same
 * algorithm and explains the design). The server hashes it again with Argon2id.
 *
 * PBKDF2-HMAC-SHA256, 210 000 iterations, salted with the account's email so
 * the same password gives a different value for every account.
 */

const ITERATIONS = 210_000
const SALT_PREFIX = 'insuronix-password-v1:'

function base64Url(bytes: ArrayBuffer): string {
  let binary = ''
  for (const b of new Uint8Array(bytes)) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export async function hashPassword(password: string, email: string): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    // WebCrypto exists only on secure origins - which every Insuronix route is.
    throw new Error('This page must be opened over HTTPS to sign in securely.')
  }
  const encoder = new TextEncoder()
  const key = await crypto.subtle.importKey('raw', encoder.encode(password.normalize('NFKC')), 'PBKDF2', false, [
    'deriveBits',
  ])
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: encoder.encode(SALT_PREFIX + email.trim().toLowerCase()),
      iterations: ITERATIONS,
    },
    key,
    256,
  )
  return `v1.${base64Url(bits)}`
}
